// src/hooks/useNavigation.ts - Navigation logic for Navbar
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';

export interface NavigationState {
  // Mobile menu state
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;

  // User dropdown state
  isUserDropdownOpen: boolean;
  setIsUserDropdownOpen: (open: boolean) => void;

  // Feature modals state
  showFlashRTC: boolean;
  setShowFlashRTC: (show: boolean) => void;
  showExecutiveTraining: boolean;
  setShowExecutiveTraining: (show: boolean) => void;
  showISO27001: boolean;
  setShowISO27001: (show: boolean) => void;
  showUpgradeModal: boolean;
  setShowUpgradeModal: (show: boolean) => void;
  upgradeFeature: { feature: 'essential' | 'gold' | 'premium', name: string } | null;
  setUpgradeFeature: (feature: { feature: 'essential' | 'gold' | 'premium', name: string } | null) => void;

  // Refs
  userDropdownRef: React.RefObject<HTMLDivElement>;
  mobileMenuRef: React.RefObject<HTMLDivElement>;

  // Navigation helpers
  isActive: (path: string) => boolean;
  handleFeatureClick: (feature: string) => void;
  handleLogout: () => void;
}

export const useNavigation = (): NavigationState => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { canAccessFeature } = useSubscriptionContext();

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // User dropdown state
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  // Feature modals state
  const [showFlashRTC, setShowFlashRTC] = useState(false);
  const [showExecutiveTraining, setShowExecutiveTraining] = useState(false);
  const [showISO27001, setShowISO27001] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<{ feature: 'essential' | 'gold' | 'premium', name: string } | null>(null);

  // Refs
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown when location changes
  useEffect(() => {
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Navigation helpers
  const isActive = (path: string) => {
    const pathname = location.pathname.replace(/\/$/, '') || '/';
    const target = path.replace(/\/$/, '') || '/';
    if (target === '/bcp') return pathname === '/bcp' || pathname.startsWith('/bcp/');
    if (target === '/tabletop-exercises') {
      return pathname === '/tabletop-exercises' || pathname.startsWith('/tabletop-exercises/');
    }
    if (target === '/crisis-communications') return pathname.startsWith('/crisis-communications');
    if (target === '/admin') return pathname === '/admin' || pathname.startsWith('/admin/');
    if (target === '/analytics-dashboard') return pathname.startsWith('/analytics-dashboard');
    if (target === '/insurer-dashboard') return pathname.startsWith('/insurer-dashboard');
    return pathname === target;
  };

  const handleFeatureClick = (feature: string) => {
    const openIsoOnWorkspace = () => {
      const legalPages = new Set([
        '/privacy-policy',
        '/terms-of-service',
        '/cookie-policy',
        '/data-protection',
        '/about',
      ]);
      if (legalPages.has(location.pathname)) {
        navigate('/bcp');
      }
      setShowISO27001(true);
    };

    switch (feature) {
      case 'flashrtc':
        if (canAccessFeature('flash_rtc') || (user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN'))) {
          window.location.href = '/crisis-communications';
        } else {
          setUpgradeFeature({ feature: 'gold', name: 'Crisis Communications' });
          setShowUpgradeModal(true);
        }
        break;
      case 'executive_training':
        if (canAccessFeature('executive_training')) {
          setShowExecutiveTraining(true);
        } else {
          setUpgradeFeature({ feature: 'premium', name: 'Executive Training' });
          setShowUpgradeModal(true);
        }
        break;
      case 'iso27001':
        // Allow access for premium subscribers or admins
        if (canAccessFeature('iso_27001_readiness') || (user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN'))) {
          openIsoOnWorkspace();
        } else {
          setUpgradeFeature({ feature: 'premium', name: 'ISO 27001 Disaster Recovery Plan' });
          setShowUpgradeModal(true);
        }
        break;
      case 'tabletop_scenarios':
        // No subscription gate here — the dashboard handles the three paths:
        // premium/addon access, available credits, or "Buy tabletop credit — $500" CTA.
        window.location.href = '/tabletop-exercises';
        break;
      default:
        break;
    }
  };

  const handleLogout = () => {
    logout();
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  return {
    // Mobile menu state
    isMobileMenuOpen,
    setIsMobileMenuOpen,

    // User dropdown state
    isUserDropdownOpen,
    setIsUserDropdownOpen,

    // Feature modals state
    showFlashRTC,
    setShowFlashRTC,
    showExecutiveTraining,
    setShowExecutiveTraining,
    showISO27001,
    setShowISO27001,
    showUpgradeModal,
    setShowUpgradeModal,
    upgradeFeature,
    setUpgradeFeature,

    // Refs
    userDropdownRef,
    mobileMenuRef,

    // Navigation helpers
    isActive,
    handleFeatureClick,
    handleLogout,
  };
};
